import { MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { useRouterState, Link } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DEFAULT_TIMEFRAME } from 'src/features/analytics/components/constants';
import { testId } from 'tests/e2e/utils/test-ids.constant';
import OrgSwitcher from 'src/features/navigation/navigation/OrgSwitcher';
import { useAuth } from 'src/features/auth/contexts/AuthContext';

import { Separator } from 'src/shared/components/ui/separator';
import SideNavButton from 'src/shared/components/ui/buttons/sidenav-button';
import IconButton from 'src/shared/components/ui/buttons/icon-button';
import { SIDE_NAV_WIDTH } from 'src/shared/constants/uiConfig.constant';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import { cn } from 'src/shared/lib/utils';
import mixpanel from 'src/shared/lib/mixpanel';
import { parseLocalStorage, saveLocalStorage } from 'src/shared/lib/localStorage.utils';
import { useOrg } from 'src/shared/contexts/OrgContext';
import BetaBadgeV2 from 'src/shared/components/ui/badges/beta-badge-v2';
import { getAfterTimestamp, getBeforeTimestamp } from 'src/shared/lib/date.utils';
import { FEATURE_FLAGS } from 'src/shared/constants/featureFlags.constant';

import {
  LIVE_PARAM,
  AFTER_TS_PARAM,
  BEFORE_TS_PARAM,
  GRAPH_PARAM,
  PROPERTY_PARAM,
} from 'src/shared/constants/urlParams.constant';
import ConditionalParent from 'src/shared/components/utils/ConditionalParent';
import DynamicIcon from 'src/shared/components/ui/icons/dynamic-icon';
import WithFeatureFlag from 'src/shared/components/utils/WithFeatureFlag';

const SidebarHeader = memo(function SidebarHeader({ expanded, domain }: { expanded: boolean; domain: string }) {
  return (
    <button data-expanded={expanded} className="flex items-center w-full data-[expanded=false]:mb-4">
      {expanded && (
        <>
          <Link to="/">
            <img
              src="/static/svg/Logo-Blue.svg"
              width={25}
              height={25}
              alt="Tekst lo@go"
              className="hover:scale-125 transition-all shrink-0"
            />
          </Link>
          <Separator orientation="vertical" className="h-5 bg-slate-300 ml-2" />
        </>
      )}

      <OrgSwitcher expanded={expanded} domain={domain} />
    </button>
  );
});

const HomeNavItem = memo(function HomeNavItem({ expanded, isActive }: { expanded: boolean; isActive: boolean }) {
  return (
    <div data-testid={testId.navigation.sideNav.home} className="relative select-none">
      <ConditionalParent
        condition={!expanded}
        parent={(_children) => (
          <WithTooltip side="right" tooltip="Home">
            {_children}
          </WithTooltip>
        )}
      >
        <Link className="relative peer" to="/">
          <button
            data-active={isActive}
            data-expanded={expanded}
            className={cn(
              'inline-flex gap-2 text-sm truncate cursor-pointer leading-none w-full items-center hover:bg-neutral-100 text-neutral-950 rounded-sm focus-visible:outline-none border border-transparent rounded-sm px-3 h-9 font-normal data-[expanded=false]:p-0 data-[expanded=false]:w-9 data-[expanded=false]:shrink-0 data-[expanded=false]:justify-center',
              isActive && 'border-neutral-200 bg-neutral-100 !text-typography-black'
            )}
            data-testid={testId.navigation.sideNav.home}
          >
            <DynamicIcon name="House" size={16} className="shrink-0" />
            {expanded && 'Home'}
          </button>
        </Link>
      </ConditionalParent>
    </div>
  );
});

const SidebarFooter = memo(
  function SidebarFooter({
    expanded,
    userName,
    userEmail,
    role,
    onLogout,
  }: {
    expanded: boolean;
    userName?: string;
    userEmail?: string;
    role?: string;
    onLogout: () => void;
  }) {
    return (
      <div>
        <Separator />
        <div
          data-expanded={expanded}
          className="flex items-center justify-between py-4 pl-2 data-[expanded=false]:py-2 data-[expanded=false]:pl-0 data-[expanded=false]:justify-center"
        >
          {expanded && (
            <div className="max-w-[150px]">
              <div className="flex items-center gap-2 max-w-full">
                <p className="truncate text-[13px] h-4">{userName}</p>
                {role === 'SALES' && <BetaBadgeV2 text="demo" />}
              </div>
              <p className="truncate !text-typography-grey text-xs h-4">{userEmail}</p>
            </div>
          )}
          <WithTooltip tooltip="Logout" side="right">
            <IconButton aria-label="logout" icon="LogOut" onClick={onLogout} />
          </WithTooltip>
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.expanded === next.expanded &&
    prev.userName === next.userName &&
    prev.userEmail === next.userEmail &&
    prev.role === next.role &&
    prev.onLogout === next.onLogout
);

export default function Sidebar({ domain }: { domain: string }) {
  const { user } = useAuth();
  const { logout } = useAuth();
  const { role, featureFlags } = useOrg();
  // TanStack Router exposes `location` (target) and `resolvedLocation` (currently rendered).
  // SideNav is global and persists across all route transitions. When navigating within
  // /flows (e.g. /flows → /flows/:flowId), we use resolvedLocation to prevent the sidebar
  // from collapsing prematurely before the flow editor loads. When entering /flows from
  // outside (e.g. /models → /flows), resolvedLocation still shows the old route, so we
  // fall back to the target location for immediate correct state.
  const pathname = useRouterState({
    select: (state) => {
      const resolved = state.resolvedLocation?.pathname ?? state.location.pathname;
      if (resolved.startsWith('/flows')) {
        return resolved;
      }
      return state.location.pathname;
    },
  });

  // Flow routes (e.g., /flows/abc123) use a separate localStorage key that defaults to collapsed.
  // Other routes use the original 'sidebar' key that defaults to expanded.
  const isFlowRoute = pathname.startsWith('/flows/') && !['/flows/templates', '/flows/code'].includes(pathname);

  console.log('[SideNav] render', { pathname, isFlowRoute });
  const storageKey = isFlowRoute ? 'automationsSidebar' : 'sidebar';
  const defaultValue = isFlowRoute ? 'collapsed' : 'expanded';

  const localState = parseLocalStorage(storageKey, defaultValue);
  const [expanded, setExpanded] = useState(localState === 'expanded');

  // When navigating between flow and non-flow routes, re-read the correct localStorage value.
  // Follows React's recommended "adjusting state during render" pattern:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevIsFlowRoute, setPrevIsFlowRoute] = useState(isFlowRoute);
  if (prevIsFlowRoute !== isFlowRoute) {
    setPrevIsFlowRoute(isFlowRoute);
    const newStorageKey = isFlowRoute ? 'automationsSidebar' : 'sidebar';
    const newDefaultValue = isFlowRoute ? 'collapsed' : 'expanded';
    setExpanded(parseLocalStorage(newStorageKey, newDefaultValue) === 'expanded');
  }

  const useFlowsRoute = featureFlags?.has(FEATURE_FLAGS.AUTOMATIONS_V12) ?? false;
  const automationsHref = useFlowsRoute ? '/flows' : '/automations';
  const automationsActive = useFlowsRoute ? pathname.includes('/flows') : pathname.includes('/automations');

  const onMessagesClick = useCallback(() => {
    mixpanel.track('Message Feed Clicked');
  }, []);

  const onModelsClick = useCallback(() => {
    mixpanel.track('Models Clicked');
  }, []);

  const onSupportClick = useCallback(() => {
    mixpanel.track('Needed Help', { origin: 'Support' });
  }, []);

  const analyticsHref = useMemo(() => {
    // Set all the default params for the analytics page to minimize flickering because of rerendering because of params being set after mounting.
    const params = new URLSearchParams();
    params.set(LIVE_PARAM, '1');
    params.set(AFTER_TS_PARAM, getAfterTimestamp(DEFAULT_TIMEFRAME).toString());
    params.set(BEFORE_TS_PARAM, getBeforeTimestamp().toString());
    params.set(GRAPH_PARAM, 'conversations');
    params.set(PROPERTY_PARAM, 'inboxes');
    return `/analytics?${params.toString()}`;
  }, []);

  const restrictedPaths = ['/apps/'];
  if (
    restrictedPaths.some((path) => pathname.startsWith(path)) ||
    (pathname.startsWith('/automations/') && !['/automations/templates', '/automations/code'].includes(pathname))
  ) {
    return null;
  }

  function toggleExpanded(expanded: boolean) {
    setExpanded(expanded);
    mixpanel.track('Sidebar Collapse/Expand', {
      action: expanded ? 'Collapse' : 'Expand',
    });
    saveLocalStorage(storageKey, expanded ? 'expanded' : 'collapsed');
  }

  function handleMainDivClick(e: MouseEvent<HTMLDivElement>) {
    if (expanded) return;
    e.stopPropagation();
    // if the click is on the main div, toggle the expanded state
    if (e.target === e.currentTarget) {
      toggleExpanded(!expanded);
    }
  }

  return (
    <div
      data-expanded={expanded}
      onClick={handleMainDivClick}
      style={{ width: `${expanded ? SIDE_NAV_WIDTH : 52}px` }}
      className={cn(
        'relative h-full shrink-0 flex flex-col justify-between',
        `border-r p-5 pb-0 transition-all
            data-[expanded=false]:p-0 data-[expanded=false]:items-center data-[expanded=false]:pt-3
            data-[expanded=false]:[&:not(:has(button:hover))]:hover:bg-neutral-100 data-[expanded=false]:[&:not(:has(button:hover))]:cursor-pointer`
      )}
    >
      <div data-expanded={expanded} className="data-[expanded=true]:space-y-8">
        <SidebarHeader expanded={expanded} domain={domain} />

        <div className="flex flex-col gap-1">
          <HomeNavItem expanded={expanded} isActive={pathname === '/'} />
          <SideNavButton
            icon="Mails"
            href="/messages"
            expanded={expanded}
            disabled={pathname.includes('/messages')}
            active={pathname.includes('/messages')}
            onClick={onMessagesClick}
            data-testid={testId.navigation.sideNav.messages}
          >
            Messages
          </SideNavButton>
          <SideNavButton
            icon="ChartArea"
            href={analyticsHref}
            expanded={expanded}
            disabled={pathname.includes('/analytics')}
            active={pathname.includes('/analytics')}
            data-testid={testId.navigation.sideNav.analytics}
          >
            Analytics
          </SideNavButton>
          <WithFeatureFlag flag="PROCESSES">
            <SideNavButton
              expanded={expanded}
              href="/processes"
              icon="Route"
              active={pathname.includes('/processes')}
              badge={featureFlags?.has(FEATURE_FLAGS.DISABLE_PROCESSES_BETA) ? undefined : 'beta'}
              data-testid={testId.navigation.sideNav.processes}
            >
              Processes
            </SideNavButton>
          </WithFeatureFlag>
          <SideNavButton
            onClick={onModelsClick}
            expanded={expanded}
            href="/models"
            icon="Tags"
            active={pathname.includes('/models')}
            data-testid={testId.navigation.sideNav.models}
          >
            Models
          </SideNavButton>
          <SideNavButton
            expanded={expanded}
            href={automationsHref}
            icon="Workflow"
            active={automationsActive}
            data-testid={testId.navigation.sideNav.automations}
          >
            Automations
          </SideNavButton>
        </div>
      </div>
      <div>
        <div className="pb-4 flex flex-col gap-1">
          <SideNavButton
            variant="secondary"
            icon="Unplug"
            expanded={expanded}
            href="/integrations"
            active={pathname.includes('/integrations')}
            data-testid={testId.navigation.sideNav.integrations}
          >
            Integrations
          </SideNavButton>

          <SideNavButton
            expanded={expanded}
            variant="secondary"
            icon="Headset"
            href="https://support.tekst.com"
            onClick={onSupportClick}
            active={false}
            data-testid={testId.navigation.sideNav.support}
          >
            Support
          </SideNavButton>

          <SideNavButton
            expanded={expanded}
            variant="secondary"
            icon="Settings"
            href="/settings"
            active={pathname.includes('/settings')}
            data-testid={testId.navigation.sideNav.settings}
          >
            Settings
          </SideNavButton>
        </div>

        <SidebarFooter
          expanded={expanded}
          userName={user?.name}
          userEmail={user?.email}
          role={role}
          onLogout={logout}
        />
      </div>

      <div
        style={{
          zIndex: 1000,
        }}
        onClick={() => toggleExpanded(!expanded)}
        title={expanded ? 'Click to collapse sidebar' : 'Click to expand sidebar'}
        className={cn(
          'w-2 h-full flex-center group/collapsehandle absolute top-0 -right-1',
          expanded ? 'cursor-w-resize' : 'cursor-e-resize'
        )}
      >
        <button className="absolute top-4 opacity-0 group-hover/collapsehandle:opacity-100 transition-opacity duration-500 left-1/2 -translate-x-1/2 bg-white border rounded-sm size-8 flex-center hover:bg-neutral-100 text-typography-black">
          {expanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="w-1 h-full group-hover/collapsehandle:bg-neutral-300 transition-all bg-transparent" />
      </div>
    </div>
  );
}
